import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { niceStep, round2 } from "./scale.js";
// ── Geometry, in viewBox units ────────────────────────────────────────────────
const VIEW_W = 240;
const VIEW_H = 84;
const PAD_LEFT = 8;
// The end-value direct label lives in PAD_RIGHT, and is measured the same way as the
// stacked chart's tick rail: at END_LABEL_FONT_SIZE, Geist Mono advances ~0.6em, so
// 40 units holds a six-character count ("12,345") without the viewport cropping it.
const PAD_RIGHT = 48;
const PAD_TOP = 16; // the own-scale line lives here
const PAD_BOTTOM = 16; // first/last period labels — inside the viewBox, never clipped
const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
const BASELINE_Y = PAD_TOP + PLOT_H;
const LINE_W = 2;
const END_LABEL_FONT_SIZE = 10;
const MARKER_R = 3.5;
const defaultFormat = (value) => new Intl.NumberFormat("en-GB").format(value);
/** Splits the series into contiguous runs of real values. Every `null` ends a run. */
function contiguousRuns(points) {
    const runs = [];
    let current = null;
    points.forEach((value, index) => {
        if (value == null) {
            current = null;
            return;
        }
        if (current == null) {
            current = [];
            runs.push({ indices: current });
        }
        current.push(index);
    });
    return runs;
}
export function TrendSmallMultiple({ points, labels, ownScale, yMax, formatValue = defaultFormat, ariaLabel, className, }) {
    if (points.length === 0)
        return null;
    if (labels.length !== points.length) {
        throw new Error(`TrendSmallMultiple: ${labels.length} labels for ${points.length} points — the ` +
            `arrays are index-aligned, so a mismatch means a period was dropped or duplicated ` +
            `between the projection and the render.`);
    }
    for (let i = 0; i < points.length; i += 1) {
        const value = points[i];
        if (value == null)
            continue;
        if (!Number.isFinite(value) || value < 0) {
            throw new Error(`TrendSmallMultiple: point ${String(value)} at "${labels[i]}" is not a finite, ` +
                `non-negative number. A gap is null — never NaN, never a negative sentinel.`);
        }
    }
    const observed = points.reduce((max, v) => (v != null && v > max ? v : max), 0);
    if (!ownScale && yMax != null && observed > yMax) {
        throw new Error(`TrendSmallMultiple: point ${observed} exceeds the shared scale max ${yMax}. The ` +
            `shared max is computed from the same cells these points come from, so this means ` +
            `they disagree — pass ownScale for a series that genuinely needs its own ceiling.`);
    }
    // niceStep never returns 0, so an all-zero (or all-gap) series still scales: its
    // points land on the baseline instead of producing NaN coordinates.
    const scaleMax = ownScale ? niceStep(observed) : (yMax ?? niceStep(observed));
    const xOf = (index) => points.length === 1
        ? PAD_LEFT + PLOT_W / 2
        : PAD_LEFT + (PLOT_W * index) / (points.length - 1);
    const yOf = (value) => BASELINE_Y - (value / scaleMax) * PLOT_H;
    const runs = contiguousRuns(points);
    const gapCount = points.filter((v) => v == null).length;
    const lastIndex = runs.length > 0 ? runs[runs.length - 1].indices.slice(-1)[0] : -1;
    const lastValue = lastIndex >= 0 ? points[lastIndex] : null;
    const label = ariaLabel ??
        `Trend line over ${points.length} ${points.length === 1 ? "period" : "periods"}, ` +
            `${labels[0]} to ${labels[labels.length - 1]}` +
            (lastValue == null ? "" : `; latest ${formatValue(lastValue)}`) +
            (gapCount === 0
                ? "."
                : `; ${gapCount} ${gapCount === 1 ? "period has" : "periods have"} no data recorded, ` +
                    `shown as a break in the line.`) +
            (ownScale ? ` Own scale, maximum ${formatValue(scaleMax)}.` : "");
    const slotW = points.length === 1 ? PLOT_W : PLOT_W / (points.length - 1);
    return (_jsx("div", { className: ["chart", "chart--trend", className ?? ""].filter(Boolean).join(" "), children: _jsxs("svg", { className: "chart-trend", role: "img", "aria-label": label, viewBox: `0 0 ${VIEW_W} ${VIEW_H}`, style: { display: "block", width: "100%", height: "auto" }, children: [ownScale ? (_jsx("text", { className: "chart-trend__own-scale", x: PAD_LEFT, y: PAD_TOP - 6, fontSize: 9, style: {
                        fill: "var(--ard-muted)",
                        fontFamily: "var(--font-ui)",
                        fontVariantNumeric: "tabular-nums",
                    }, children: `${ownScale.label} · max ${formatValue(scaleMax)}` })) : null, _jsx("line", { className: "chart-trend__baseline", x1: PAD_LEFT, y1: BASELINE_Y, x2: PAD_LEFT + PLOT_W, y2: BASELINE_Y, vectorEffect: "non-scaling-stroke", style: { stroke: "var(--ard-border)" }, strokeWidth: 1 }), runs.map((run) => {
                    const d = run.indices
                        .map((index, position) => {
                        const command = position === 0 ? "M" : "L";
                        return `${command}${round2(xOf(index))} ${round2(yOf(points[index]))}`;
                    })
                        .join(" ");
                    return (_jsx("path", { className: "chart-trend__line", "data-from": labels[run.indices[0]], "data-to": labels[run.indices[run.indices.length - 1]], d: run.indices.length === 1 ? `${d} ${d.replace(/^M/, "L")}` : d, vectorEffect: "non-scaling-stroke", style: { fill: "none", stroke: "var(--ard-trend)" }, strokeWidth: LINE_W, strokeLinecap: "round", strokeLinejoin: "round" }, `run-${run.indices[0]}`));
                }), runs
                    .filter((run) => run.indices.length === 1)
                    .map((run) => {
                    const index = run.indices[0];
                    return (_jsx("circle", { className: "chart-trend__isolated", cx: round2(xOf(index)), cy: round2(yOf(points[index])), r: MARKER_R, style: { fill: "var(--ard-trend)" } }, `isolated-${index}`));
                }), lastIndex >= 0 ? (_jsxs(_Fragment, { children: [_jsx("circle", { className: "chart-trend__end-dot", cx: round2(xOf(lastIndex)), cy: round2(yOf(lastValue)), r: MARKER_R, style: { fill: "var(--ard-trend)", stroke: "var(--ard-surface)" }, strokeWidth: 2 }), _jsx("text", { className: "chart-trend__end-label", x: round2(xOf(lastIndex) + 8), y: round2(yOf(lastValue)), dominantBaseline: "middle", fontSize: END_LABEL_FONT_SIZE, style: {
                                fill: "var(--ard-text)",
                                fontFamily: "var(--font-mono)",
                                fontVariantNumeric: "tabular-nums",
                            }, children: formatValue(lastValue) })] })) : null, _jsx("text", { className: "chart-trend__period-label", x: PAD_LEFT, y: BASELINE_Y + 11, fontSize: 8, style: { fill: "var(--ard-muted)", fontFamily: "var(--font-ui)" }, children: labels[0] }), points.length > 1 ? (_jsx("text", { className: "chart-trend__period-label", x: PAD_LEFT + PLOT_W, y: BASELINE_Y + 11, textAnchor: "end", fontSize: 8, style: { fill: "var(--ard-muted)", fontFamily: "var(--font-ui)" }, children: labels[labels.length - 1] })) : null, _jsx("g", { className: "chart-trend__hits", children: points.map((value, index) => {
                        const centre = xOf(index);
                        const left = Math.max(PAD_LEFT, centre - slotW / 2);
                        const right = Math.min(PAD_LEFT + PLOT_W, centre + slotW / 2);
                        return (_jsx("rect", { className: "chart-trend__hit", "data-index": String(index), x: round2(left), y: PAD_TOP, width: round2(Math.max(0, right - left)), height: PLOT_H, style: { fill: "transparent" }, children: _jsx("title", { children: `${labels[index]}: ${value == null ? "no data recorded" : formatValue(value)}` }) }, `hit-${index}`));
                    }) })] }) }));
}
export default TrendSmallMultiple;
//# sourceMappingURL=TrendSmallMultiple.js.map